import {
  Badge,
  Button,
  Column,
  FlexBox,
  Icon,
  Page,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useToggle, useUpdateEffect } from 'react-use';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { SearchControls } from 'components/teamPageNew/components/searchControls';
import { useTeamTableData } from 'components/teamPageNew/hooks/useTeamTableData';
import { useTeamTableFilters } from 'components/teamPageNew/hooks/useTeamTableFilters';
import { twilioActions } from 'middleware/twilio/actions/twilioActions';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { TeamPage } from './components/teamPage';

export const TeamView = (): JSX.Element => {
  const { legacyPresenceSidebar } = useFeatureFlag(['legacy-presence-sidebar']);
  const dispatch = useAppDispatch();
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const [hasCollapsedControls, toggleHasCollapsedControls] =
    useToggle(!!isSmallScreen);

  useUpdateEffect(() => {
    toggleHasCollapsedControls(!!isSmallScreen);
  }, [isSmallScreen]);

  const isMicrophoneBlocked = useAppSelector(
    (state) => state.telephony.isMicrophoneBlocked,
  );

  const twilioMakeInternalCall = useCallback(
    (userId: string) =>
      bindActionToPromise(
        dispatch,
        twilioActions.twilioMakeInternalCall,
      )(userId),
    [dispatch],
  );

  const hasConversationReplyAccess = useHasAccess('conversation.reply');

  const { page, activeFilters, setPage, setActiveFilters, clearFilters } =
    useTeamTableFilters('/team');

  const {
    ownId,
    favorites,
    membersWithPresence,
    memberCount,
    pageCount,
    loading,
    isManualRefresh,
    handleRefreshMembers,
    toggleFavorite,
    showUserModal,
    hasOrganizationMemberAccess,
    hasPresenceAccess,
    awayStatuses,
  } = useTeamTableData({ page, activeFilters });

  // Expansion state
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const [
    readSearchConversations,
    { results: conversations, isFetching: conversationsLoading },
  ] = useLazyReadSearchInfiniteQuery<'conversations', true>();

  useEffect(() => {
    if (!expandedMemberId) return;

    readSearchConversations({
      scope: 'conversations',
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'agent_id',
            operator: 'is_one',
            values: [{ value: expandedMemberId }],
          },
          {
            field: 'status',
            operator: 'is_one',
            values: [{ value: 'open' }],
          },
        ],
      },
      enrichmentSpec: 'ALL',
    });
  }, [expandedMemberId, readSearchConversations]);

  const toggleRowExpanded = useCallback(
    (memberId: string) => (e: MouseEvent<HTMLElement>) => {
      const clickedOnDropdownButton = !!(e.target as HTMLElement).closest(
        '[data-dropdown-button="true"]',
      );
      if (clickedOnDropdownButton) {
        return;
      }
      const open = expandedMemberId !== memberId;

      open ? ampli.teamAgentExpanded() : ampli.teamAgentCollapsed();
      open ? setExpandedMemberId(memberId) : setExpandedMemberId(null);
    },
    [expandedMemberId],
  );

  const controls = (
    <SearchControls
      activeFilters={activeFilters}
      setActiveFilters={setActiveFilters}
      onRefreshMembers={handleRefreshMembers}
      isRefreshing={isManualRefresh}
    />
  );

  const toggleControlsButton = isSmallScreen ? (
    <Button
      variant="tertiary"
      slotStart={<Icon name={hasCollapsedControls ? 'plus' : 'minus'} />}
      onClick={toggleHasCollapsedControls}
    >
      {hasCollapsedControls ? 'Show filters' : 'Hide filters'}
    </Button>
  ) : null;

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            Team
            {memberCount != null && (
              <Badge size="medium" variant="secondary">
                {memberCount}
              </Badge>
            )}
          </FlexBox>
        }
        controls={isSmallScreen ? toggleControlsButton : controls}
      />

      {isSmallScreen && !hasCollapsedControls && (
        <Column.Row noMinHeight>{controls}</Column.Row>
      )}

      <Page.Body>
        <TeamPage
          members={membersWithPresence}
          memberCount={memberCount}
          pageCount={pageCount}
          loading={loading}
          page={page}
          setPage={setPage}
          activeFilters={activeFilters}
          clearFilters={clearFilters}
          favorites={favorites}
          ownId={ownId}
          toggleFavorite={toggleFavorite}
          showFavorites={!legacyPresenceSidebar}
          showUserModal={showUserModal}
          awayStatuses={awayStatuses}
          hasPresenceAccess={hasPresenceAccess}
          hasOrganizationMemberAccess={hasOrganizationMemberAccess}
          tableWrapperRef={tableWrapperRef}
          expandedMemberId={expandedMemberId}
          onToggleExpand={toggleRowExpanded}
          conversations={conversations}
          conversationsLoading={conversationsLoading}
          twilioMakeInternalCall={twilioMakeInternalCall}
          isMicrophoneBlocked={isMicrophoneBlocked}
          hasConversationReplyAccess={hasConversationReplyAccess}
        />
      </Page.Body>
    </Page>
  );
};
